import { redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequestURL } from "@tanstack/react-start/server";
import * as client from "openid-client";

import { logout } from "@onesolution/serverFn";
import { getKeycloakConfig } from "@onesolution/services";
import {
	cleanSessions,
	getAuthSessionData,
	updateAuthSession,
	updateUserSession,
} from "@onesolution/utils";

export const handleAuthCallback = createServerFn().handler(async () => {
	const currentURL = getRequestURL({
		xForwardedHost: true,
		xForwardedProto: true,
	});
	const config = await getKeycloakConfig();
	const authSession = await getAuthSessionData();
	const { code_verifier, state } = authSession;

	if (!code_verifier || !state) {
		if (import.meta.env.DEV) console.error("Missing code_verifier or state");

		await cleanSessions();
		throw redirect({ to: "/login", replace: true });
	}

	currentURL.searchParams.append("state", state);

	let tokens: Awaited<ReturnType<typeof client.authorizationCodeGrant>>;
	let userDetails: ReturnType<(typeof tokens)["claims"]>;

	try {
		tokens = await client.authorizationCodeGrant(config, currentURL, {
			pkceCodeVerifier: code_verifier,
			expectedState: state,
		});
		userDetails = tokens.claims();

		if (import.meta.env.DEV) {
			console.log("Token login", tokens);
		}
	} catch (error) {
		await cleanSessions();

		return logout();
	}

	try {
		await updateAuthSession({
			data: {
				access_token: tokens.access_token,
				expires_in: Date.now() + (tokens.expires_in || 0) * 1000,
				refresh_token: tokens.refresh_token,
				code_verifier: undefined,
				state: undefined,
			},
		});

		await updateUserSession({
			data: {
				id_token: tokens.id_token,
				isAuthenticated: true,
				...userDetails,
			},
		});
	} catch (error) {
		await cleanSessions();

		return logout();
	}

	throw redirect({ to: "/", replace: true });
});
